import { useMemo } from 'react';

/**
 * Hook for matching inline address fields to a contact object
 * Attempts to find an existing contact that matches the inline fields
 *
 * @param {Object} inlineFields - Object with inline address fields
 * @param {string} inlineFields.name - Contact name
 * @param {string} inlineFields.email - Contact email
 * @param {string} inlineFields.address - Street address
 * @param {string} inlineFields.city - City
 * @param {string} inlineFields.state - State
 * @param {string} inlineFields.zip - ZIP code
 * @param {Array} contacts - Array of available contacts to match against
 * @returns {Object|null} - Matched contact or synthetic contact object if no match found
 */
export const useContactMatcher = (inlineFields, contacts = []) => {
  const matchedContact = useMemo(() => {
    // Need at least a name to match
    if (!inlineFields || !inlineFields.name) {
      return null;
    }

    const normalize = str => (str || '').trim().toLowerCase();
    const normalizeAddr2 = val => val || '';

    // Helper to check if address fields match
    const addressFieldsMatch = contact => {
      const inlineAddr2 = normalizeAddr2(inlineFields.address_2);
      const contactAddr2 = normalizeAddr2(contact.address_2);

      return (
        (!inlineFields.address || contact.address === inlineFields.address) &&
        inlineAddr2 === contactAddr2 &&
        (!inlineFields.city || contact.city === inlineFields.city) &&
        (!inlineFields.state || contact.state === inlineFields.state) &&
        (!inlineFields.zip || contact.zipcode === inlineFields.zip)
      );
    };

    // Strategy 1: Match by email + address (if email exists and matches)
    if (inlineFields.email) {
      const emailMatch = contacts.find(c => c.email && normalize(c.email) === normalize(inlineFields.email));
      if (emailMatch && addressFieldsMatch(emailMatch)) {
        return emailMatch;
      }
      // If email match found but address doesn't match, continue to Strategy 2
    }

    // Strategy 2: Match by name + address (fallback when email doesn't match or doesn't exist)
    // This handles cases where inline has mismatched email or contact has no email
    if (inlineFields.address && inlineFields.city) {
      const nameMatches = c => {
        const contactName = normalize(c.name);
        const contactBusinessName = normalize(c.business_name);
        const inlineName = normalize(inlineFields.name);
        const inlineBusinessName = normalize(inlineFields.business_name);

        // Match if names match OR business_names match (when both exist)
        return (
          (contactName && inlineName && contactName === inlineName) ||
          (contactBusinessName && inlineBusinessName && contactBusinessName === inlineBusinessName)
        );
      };

      const nameAddressMatch = contacts.find(c => nameMatches(c) && addressFieldsMatch(c));
      if (nameAddressMatch) {
        return nameAddressMatch;
      }
    }

    // No match found - return synthetic contact
    return {
      id: null,
      name: inlineFields.name,
      business_name: inlineFields.business_name || '',
      email: inlineFields.email || '',
      phone1: inlineFields.phone || '',
      address: inlineFields.address || '',
      address_2: inlineFields.address_2 || '',
      city: inlineFields.city || '',
      state: inlineFields.state || '',
      zipcode: inlineFields.zip || '',
      country: inlineFields.country || '',
      full_address_display: [
        inlineFields.address,
        inlineFields.address_2,
        [inlineFields.city, inlineFields.state, inlineFields.zip].filter(Boolean).join(', '),
        inlineFields.country,
      ]
        .filter(Boolean)
        .join(', '),
      _isSynthetic: true,
    };
  }, [inlineFields, contacts]);

  return matchedContact;
};
